/**
 * Thematic — emergent smart-money themes from specialist co-accumulation.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ExternalLink, Loader2, RefreshCw, Sparkles, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { API } from "../components/stats/statsApi";
import { WorkingThemesView } from "../components/WorkingThemesView";
import { FlowTimeline, RecentMovementsStrip } from "../components/ThemeFlows";
import { ThemeGlossaryDialog } from "../components/ThemeGlossaryDialog";

const pct = (v, dp = 0) => (v == null ? "—" : `${(v * 100).toFixed(dp)}%`);
const cap = (v) => {
  if (!v) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  return `$${(v / 1e6).toFixed(0)}M`;
};

const STAGE_STYLE = {
  Accumulating: "text-emerald-400 border-emerald-500/40",
  Igniting: "text-amber-400 border-amber-500/40",
  Mixed: "text-muted-foreground border-border",
  Distributing: "text-red-400 border-red-500/40",
};

const ScoreChip = ({ label, value, help }) => (
  <span className="px-2 py-0.5 rounded bg-secondary/60 font-mono text-[10px]" title={help}>
    <span className="text-muted-foreground">{label}</span> {value}
  </span>
);

const CHIP_HELP = {
  members: "Tradeable basket size (max 7, tape-first selection)",
  specialists: "Distinct top-20%-skill funds that bought members this window",
  "median cap": "Median member market cap",
  coherence: "Mean pairwise business-DNA similarity — higher = one clean trade",
  conviction: "Fund count + buyer skill blend (descriptive only, no forward edge)",
  acceleration: "Recent-half vs prior-half buying funds; >1x = adoption accelerating (+5.4%/3M validated)",
  quiet: "Members with quiet options tape — the strongest forward signal",
  "strong regimes": "Members in strong accumulation regimes (nowcast overlay)",
  hiddenness: "Median inverse market-cap percentile (descriptive only)",
};

const STAGE_HELP = {
  Accumulating: "≥50% members in strong regimes with quiet tape — being built quietly (best entry stage)",
  Igniting: "≥40% strong regimes, tape heating — market starting to notice",
  Mixed: "No regime dominates",
  Distributing: "≥40% members in Overcrowded Peak / Distribution regimes — smart money likely exiting",
};

const LinkageTags = ({ tags }) =>
  tags?.length > 0 && (
    <div className="flex gap-1 flex-wrap mt-1.5" data-testid="linkage-tags">
      {tags.map((tg) => (
        <span key={tg} className="px-1.5 py-0.5 rounded border border-amber-500/30 text-amber-300/90 text-[10px] font-mono">
          {tg}
        </span>
      ))}
    </div>
  );

const MembersTable = ({ members, themeIdx }) => (
  <div className="overflow-x-auto mt-3">
    <table className="w-full text-xs" data-testid={`theme-members-${themeIdx}`}>
      <thead className="border-b border-border text-muted-foreground">
        <tr>
          <th className="py-1.5 px-2 text-left">Symbol</th>
          <th className="py-1.5 px-2 text-left">Company</th>
          <th className="py-1.5 px-2 text-left">Role in theme</th>
          <th className="py-1.5 px-2 text-left">Industry</th>
          <th className="py-1.5 px-2 text-right">Mkt cap</th>
          <th className="py-1.5 px-2 text-right">Specialists</th>
          <th className="py-1.5 px-2 text-right">Buyer skill</th>
          <th className="py-1.5 px-2 text-left">Regime</th>
          <th className="py-1.5 px-2 text-left">Tape</th>
          <th className="py-1.5 px-2 text-right">Kensys upside</th>
          <th className="py-1.5 px-2 text-right">Torque</th>
        </tr>
      </thead>
      <tbody>
        {members.map((m) => (
          <tr key={m.symbol} className="border-b border-border/40">
            <td className="py-1.5 px-2 font-semibold">
              {m.symbol}
              {m.crowded && (
                <span className="ml-1 px-1 py-0.5 rounded border border-red-500/40 text-red-400 text-[9px] font-mono align-middle"
                      title="Top-decile crowding, not in a strong regime (−2.34%/3M vs SPY, t −3.49)"
                      data-testid={`crowded-badge-${m.symbol}`}>CROWDED</span>
              )}
            </td>
            <td className="py-1.5 px-2 text-muted-foreground max-w-[180px] truncate" title={m.name}>{m.name || "—"}</td>
            <td className="py-1.5 px-2 text-amber-200/80 max-w-[230px] truncate" title={m.role}>{m.role || "—"}</td>
            <td className="py-1.5 px-2 text-muted-foreground max-w-[140px] truncate">{m.industry || "—"}</td>
            <td className="py-1.5 px-2 text-right font-mono">{cap(m.market_cap)}</td>
            <td className="py-1.5 px-2 text-right font-mono">{m.n_specialists}</td>
            <td className="py-1.5 px-2 text-right font-mono">{m.buyer_skill ?? "—"}</td>
            <td className={`py-1.5 px-2 ${m.strong ? "text-emerald-400" : m.regime ? "text-amber-400" : "text-muted-foreground"}`}>
              {m.regime || "—"}
            </td>
            <td className={`py-1.5 px-2 capitalize ${
              m.tape === "quiet" ? "text-emerald-400" : m.tape === "hot" ? "text-red-400" : "text-muted-foreground"}`}>
              {m.tape || "—"}
            </td>
            <td className="py-1.5 px-2 text-right font-mono">
              {m.kensys_upside != null ? pct(m.kensys_upside) : "—"}
            </td>
            <td className="py-1.5 px-2 text-right font-mono font-semibold">{m.torque?.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ThemeCard = ({ t, open, onToggle, isTopPick, flowData }) => {
  const s = t.scores || {};
  return (
    <Card data-testid={`theme-card-${t.idx}`}>
      <CardContent className="p-4">
        <div className="w-full text-left cursor-pointer" role="button" onClick={onToggle}
             data-testid={`theme-toggle-${t.idx}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              <div>
                <div className="font-bold text-base flex items-center gap-2">
                  {t.name}
                  {isTopPick && (
                    <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 text-[10px]"
                           data-testid={`top-pick-${t.idx}`}
                           title="Top third by calibrated score — showcase themes historically +6.9%/3M vs SPY vs +2.2% for the rest">★ SHOWCASE</Badge>
                  )}
                  {s.pre_ex_6m != null && s.pre_ex_6m < 0 && (
                    <Badge className="bg-sky-500/15 text-sky-300 border border-sky-500/40 text-[10px]"
                           data-testid={`undiscovered-${t.idx}`}
                           title="Basket LAGGED SPY over the 6 months before formation — specialists accumulating before the market notices. Strongest validated setup: +5.7%/3M forward (t 4.0)">UNDISCOVERED</Badge>
                  )}
                  {t.late_stage && (
                    <Badge className="bg-red-500/15 text-red-300 border border-red-500/40 text-[10px] inline-flex items-center gap-1"
                           data-testid={`late-stage-${t.idx}`}
                           title="Graph connectivity accelerating vs last run — validated crowding marker (rising-connectivity themes lag by ~6%/3M)">
                      <TrendingUp className="w-3 h-3" /> LATE-STAGE
                    </Badge>
                  )}
                </div>
                {t.thesis && (
                  <p className="text-xs text-muted-foreground max-w-3xl mt-0.5">{t.thesis}</p>
                )}
                <LinkageTags tags={t.linkage_tags} />
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {s.freshness && (
                <Badge variant="outline"
                       className={`text-[10px] font-mono ${
                         s.freshness === "fresh" ? "text-emerald-400 border-emerald-500/40"
                           : s.freshness === "aging" ? "text-amber-400 border-amber-500/40"
                           : "text-red-400 border-red-500/40"}`}
                       data-testid={`freshness-${t.idx}`}
                       title={`health ${s.health} — tape/stability/buy-recency/trailing perf`}>
                  {s.freshness} · {s.health}
                </Badge>
              )}
              <Badge variant="outline" className="text-[10px] font-mono"
                     title="Formation runs this lineage has persisted (identity kept via ≥40% member overlap)">
                {t.status === "new" || t.status === "split" ? "NEW"
                  : `${t.quarters_alive || 1}Q ${t.status === "merged" ? "· merged" : ""}`}
              </Badge>
              <Badge variant="outline" className={`text-[10px] ${STAGE_STYLE[s.lifecycle] || ""}`}
                     title={STAGE_HELP[s.lifecycle]}>
                {s.lifecycle || "—"}
              </Badge>
              <span className="font-mono text-sm font-bold"
                    title="Calibrated theme score: 0.25 accel + 0.45 quiet% + 0.20 strong% + 0.05 conviction + 0.05 hiddenness — top-2/date earned +6.32%/3M (t 4.3)">{s.theme_score?.toFixed(2)}</span>
              {t.theme_id && (
                <Link to={`/thematic/${t.theme_id}`} onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-[11px] text-sky-400 hover:text-sky-300 border border-sky-500/30 rounded px-2 py-0.5"
                      data-testid={`deep-dive-${t.idx}`}>
                  Deep dive <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap mt-2 ml-6">
            <ScoreChip label="members" value={t.n_members} help={CHIP_HELP.members} />
            <ScoreChip label="specialists" value={t.n_specialists} help={CHIP_HELP.specialists} />
            <ScoreChip label="median cap" value={cap(t.median_cap)} help={CHIP_HELP["median cap"]} />
            <ScoreChip label="coherence" value={s.coherence ?? "—"} help={CHIP_HELP.coherence} />
            <ScoreChip label="conviction" value={s.conviction} help={CHIP_HELP.conviction} />
            <ScoreChip label="acceleration" value={`${s.accel_raw ?? "—"}x`} help={CHIP_HELP.acceleration} />
            <ScoreChip label="quiet" value={pct(s.quiet_pct)} help={CHIP_HELP.quiet} />
            <ScoreChip label="strong regimes" value={pct(s.strong_pct)} help={CHIP_HELP["strong regimes"]} />
            <ScoreChip label="hiddenness" value={s.hiddenness} help={CHIP_HELP.hiddenness} />
          </div>
        </div>
        {open && (
          <>
            {t.linkage && (
              <p className="text-xs text-amber-200/70 mt-3 ml-6 max-w-4xl border-l-2 border-amber-500/30 pl-3"
                 data-testid={`theme-linkage-${t.idx}`}>
                <span className="font-semibold text-amber-300/90">Why one trade: </span>{t.linkage}
              </p>
            )}
            <MembersTable members={t.members} themeIdx={t.idx} />
            {flowData?.flows?.length > 1 && (
              <FlowTimeline flows={flowData.flows} testid={`theme-flows-${t.idx}`} />
            )}
            {t.bench?.length > 0 && (
              <p className="text-[11px] text-muted-foreground mt-2 font-mono" data-testid={`theme-bench-${t.idx}`}>
                Bench (in cluster, outside the tradeable basket): {t.bench.join(", ")}
              </p>
            )}
            {t.affiliates?.length > 0 && (
              <p className="text-[11px] text-muted-foreground mt-2 font-mono">
                Cross-theme links: {t.affiliates.map((a) => a.symbol).join(", ")}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default function ThemesPage() {
  const { token } = useAuth();
  const [run, setRun] = useState(null);
  const [flows, setFlows] = useState(null);
  const [error, setError] = useState(null);
  const [computing, setComputing] = useState(false);
  const [open, setOpen] = useState({});
  const [mode, setMode] = useState("discovery");
  const [show, setShow] = useState("showcase");
  const [freshF, setFreshF] = useState("all");
  const [capF, setCapF] = useState("all");
  const poll = useRef(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    fetch(`${API}/themes/flows`, auth)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setFlows(d))
      .catch(() => {});
    const res = await fetch(`${API}/themes`, auth);
    if (res.status === 404) { setError("empty"); return; }
    if (!res.ok) { setError(`HTTP ${res.status}`); return; }
    setRun(await res.json());
    setError(null);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const watchJob = useCallback(() => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const res = await fetch(`${API}/themes/status`, auth);
      const st = await res.json();
      if (!st.running) {
        clearInterval(poll.current);
        setComputing(false);
        if (st.error) toast.error(`Compute failed: ${st.error}`);
        else { toast.success("Themes recomputed"); await load(); }
      }
    }, 5000);
  }, [token, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const compute = async () => {
    setComputing(true);
    const res = await fetch(`${API}/themes/compute`, { method: "POST", ...auth });
    if (!res.ok) { setComputing(false); toast.error("Failed to start"); return; }
    toast.info("Theme discovery running — graph + LLM labeling (~1-2 min)");
    watchJob();
  };

  useEffect(() => {
    if (!token) return;
    load();
    return () => clearInterval(poll.current);
  }, [token, load]);

  return (
    <div className="p-6 space-y-6 max-w-[1500px]" data-testid="themes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-400" /> Thematic
          </h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Emergent themes discovered bottom-up from what skilled specialists co-accumulate —
            no predefined labels. Co-buy graph edges are damped by business-DNA similarity
            (LLM-structured exposure tags per company), so every cluster is one coherent trade.
            Ranked by a score calibrated on forward returns.
          </p>
          {run && (
            <p className="text-[11px] font-mono text-muted-foreground mt-1">
              computed {run.computed_at?.slice(0, 10)} · {run.n_specialist_buys} specialist buys ·
              graph {run.graph?.nodes}n/{run.graph?.edges}e · overlay {run.overlay_as_of || "—"}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ThemeGlossaryDialog />
          <div className="flex rounded-md border border-border overflow-hidden" data-testid="themes-mode-toggle">
            {[["discovery", "Discovery"], ["working", "Working Now"]].map(([k, label]) => (
              <button key={k} onClick={() => setMode(k)}
                      data-testid={`mode-${k}`}
                      className={`px-3 py-1.5 text-xs font-mono transition-colors ${
                        mode === k ? "bg-amber-500/15 text-amber-300"
                                   : "text-muted-foreground hover:text-foreground"}`}>
                {k === "working" ? "🔥 " : ""}{label}
              </button>
            ))}
          </div>
          {mode === "discovery" && (
            <Button onClick={compute} disabled={computing} data-testid="themes-compute-btn">
              {computing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Discovering…</>
                         : <><RefreshCw className="w-4 h-4 mr-1" />Recompute themes</>}
            </Button>
          )}
        </div>
      </div>

      {flows?.recent?.length > 0 && (
        <RecentMovementsStrip recent={flows.recent} latestRun={flows.latest_run} />
      )}

      {mode === "working" && (
        <WorkingThemesView flowsById={Object.fromEntries(
          (flows?.themes || []).map((f) => [f.theme_id, f]))} />
      )}

      {mode === "discovery" && <>
      {error === "empty" && !computing && (
        <Card><CardContent className="p-8 text-center text-muted-foreground text-sm">
          No theme run yet — click "Recompute themes" to run the first discovery.
        </CardContent></Card>
      )}
      {error && error !== "empty" && <p className="text-sm text-red-400">{error}</p>}

      {run && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="theme-filters">
          {[["showcase", `Showcase (${(run.themes || []).filter((t) => t.gate).length})`],
            ["all", `All (${(run.themes || []).length})`]].map(([k, label]) => (
            <button key={k} onClick={() => setShow(k)}
                    data-testid={`filter-${k}`}
                    className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${
                      show === k ? "border-amber-500/60 text-amber-300 bg-amber-500/10"
                                 : "border-border text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
          <span className="w-px h-4 bg-border mx-1" />
          {[["all", "Any freshness"], ["fresh", "Fresh"], ["aging", "Aging / Stale"]].map(([k, label]) => (
            <button key={k} onClick={() => setFreshF(k)}
                    data-testid={`filter-fresh-${k}`}
                    className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${
                      freshF === k ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                                   : "border-border text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
          <span className="w-px h-4 bg-border mx-1" />
          {[["all", "Any cap"], ["midsmall", "Mid / Small (<$10B)"]].map(([k, label]) => (
            <button key={k} onClick={() => setCapF(k)}
                    data-testid={`filter-cap-${k}`}
                    className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${
                      capF === k ? "border-sky-500/60 text-sky-300 bg-sky-500/10"
                                 : "border-border text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {(run?.themes || [])
          .filter((t) => (show === "all" || t.gate)
            && (freshF === "all"
              || (freshF === "fresh" ? t.scores?.freshness === "fresh"
                                     : t.scores?.freshness !== "fresh"))
            && (capF === "all" || (t.median_cap && t.median_cap < 10e9)))
          .map((t) => (
            <ThemeCard key={t.idx} t={t} open={!!open[t.idx]} isTopPick={!!t.gate}
                       flowData={t.theme_id ? (flows?.themes || []).find((f) => f.theme_id === t.theme_id) : null}
                       onToggle={() => setOpen((o) => ({ ...o, [t.idx]: !o[t.idx] }))} />
          ))}
      </div>

      {run && (
        <p className="text-[11px] text-muted-foreground font-mono max-w-4xl" data-testid="themes-evidence">
          Evidence — 36 monthly point-in-time formations (Jul 2023 → Jun 2026), fwd 3M EW vs SPY:
          all themes +3.9%/3M (t 3.0); top-2 by score +6.3%/3M (t 4.3), quiet-dominant weighting —
          being EARLY (quiet options tape) is the strongest validated selector. SHOWCASE gate
          (top-third by score): +6.9%/3M (t 4.0) vs +2.2% for the rest. UNDISCOVERED
          (basket lagged SPY over prior 6M): +5.7%/3M (t 4.0) vs +2.5%. Accelerating adoption:
          +5.4%/3M (t 3.3) vs 0.0%. The all-specialist-buys universe itself is flat vs SPY —
          the edge is the theme structure. Score = 0.45·quiet + 0.25·acceleration
          + 0.20·strong regimes + 0.05·conviction + 0.05·hiddenness. Quarterly replay since 2023:
          53 lineages, live themes avg +7.6% excess; best lineage 9Q alive, +170% vs SPY.
        </p>
      )}
      </>}
    </div>
  );
}
